import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Button from '../components/common/Button';
import JobContentBlocks from '../components/jobs/JobContentBlocks';
import JobMetadataGrid from '../components/jobs/JobMetadataGrid';
import { publicHiringStatus } from '../data/hiringStatus';
import PageHero from '../components/layout/PageHero';
import { getPublishedJob } from '../services/jobService';

function buildApplyPath(job) {
  const baseApplyUrl = job.applyUrl || '/apply';
  const separator = baseApplyUrl.includes('?') ? '&' : '?';

  return `${baseApplyUrl}${separator}job=${encodeURIComponent(job.slug)}`;
}

function JobDetailPage() {
  const { categorySlug, jobSlug } = useParams();
  const [state, setState] = useState({
    loading: true,
    error: '',
    hiringStatus: publicHiringStatus,
    job: null,
  });

  useEffect(() => {
    let isMounted = true;

    getPublishedJob(categorySlug, jobSlug)
      .then((data) => {
        if (isMounted) {
          setState({
            loading: false,
            error: '',
            hiringStatus: data.hiringStatus || publicHiringStatus,
            job: data.job,
          });
        }
      })
      .catch(() => {
        if (isMounted) {
          setState({
            loading: false,
            error: 'Job not found.',
            hiringStatus: publicHiringStatus,
            job: null,
          });
        }
      });

    return () => {
      isMounted = false;
    };
  }, [categorySlug, jobSlug]);

  if (state.loading) {
    return (
      <>
        <PageHero eyebrow="CAREERS" title="Loading opening" description="Fetching the latest role details." />
        <section className="section jobs-page">
          <div className="container">
            <p className="jobs-empty">Loading job details...</p>
          </div>
        </section>
      </>
    );
  }

  if (state.error || !state.job) {
    return (
      <>
        <PageHero eyebrow="CAREERS" title="Job not found" description="This opening is unavailable or no longer published." />
        <section className="section jobs-page">
          <div className="container">
            <div className="jobs-empty-card">
              <h2>Opening unavailable</h2>
              <p>Please return to Current Openings to see active vacancies.</p>
              <Button to="/jobs">Back to Current Openings</Button>
            </div>
          </div>
        </section>
      </>
    );
  }

  const job = state.job;
  const acceptingApplications = state.hiringStatus.acceptingApplications === true;
  const closedLabel = state.hiringStatus.closedLabel || publicHiringStatus.closedLabel;

  return (
    <>
      <PageHero eyebrow={job.category.name} title={job.title} description={job.shortSummary} />
      <section className="section jobs-page job-detail-page">
        <div className="container job-detail-layout">
          <aside className="job-detail-sidebar">
            <span>{job.category.name}</span>
            <h2>{job.title}</h2>
            {!acceptingApplications ? (
              <span className="hiring-status-badge hiring-status-badge--closed">{closedLabel}</span>
            ) : null}
            {acceptingApplications ? (
              <Button to={buildApplyPath(job)}>Apply Now</Button>
            ) : (
              <Button disabled>Hiring Closed</Button>
            )}
            <Button to="/jobs" variant="secondary">
              Back to Current Openings
            </Button>
          </aside>

          <article className="job-detail-card">
            <JobMetadataGrid metadata={job.metadata} />
            <JobContentBlocks blocks={job.contentBlocks} html={job.contentHtml} />
            <div className="job-actions">
              {acceptingApplications ? (
                <Button to={buildApplyPath(job)}>Apply Now</Button>
              ) : (
                <Button disabled>Hiring Closed</Button>
              )}
              <Button to="/jobs" variant="secondary">
                Back to Current Openings
              </Button>
            </div>
          </article>
        </div>
      </section>
    </>
  );
}

export default JobDetailPage;
